import { ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnDestroy, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { OLMapService } from 'src/app/services/ol-map/ol-map.service';
import { Subscription } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { setBackgroudTable, unsubscribe } from 'src/app/utils/helpers';
import { EventsKey } from 'ol/events.js';
import { unByKey } from 'ol/Observable.js';

interface LandlinePanelRow {
  provider: string;
  technology: string;
  download: string;
  upload: string;
  operators: string;
}

@Component({
  standalone: false,
  selector: 'app-ol-map',
  templateUrl: './ol-map.component.html',
  styleUrls: ['./ol-map.component.scss']
})
export class OlMapComponent implements OnInit, OnDestroy {

  @ViewChild('map', { static: true }) private mapElement?: ElementRef<HTMLElement>;
  subscriptions: Subscription[] = [];
  private clickKey?: EventsKey;
 private mapResizeObserver?: ResizeObserver;

  get showDetailPanel(): boolean {
    return this.olMapService.detailPanelOpen
      && (this.isLandlinePointDetails || (this.olMapService.details?.length ?? 0) > 0);
  }

  get isLandlinePointDetails(): boolean {
    return (this.olMapService.landlinePointDetails?.length ?? 0) > 0;
  }

  get landlineRows(): LandlinePanelRow[] {
    const seenRows = new Set<string>();

    return this.olMapService.landlinePointDetails
      .map((detail) => ({
        provider: String(detail.Provider ?? ''),
        technology: String(detail.Technology ?? ''),
        download: String(detail.Download ?? ''),
        upload: String(detail.Upload ?? ''),
        operators: String(detail.Operators ?? ''),
      }))
      .filter((row) => {
        const rowKey = JSON.stringify([
          row.provider,
          row.technology,
          row.download,
          row.upload,
          row.operators,
        ].map((value) => value.trim().replace(/\s+/g, ' ').toLocaleLowerCase()));

        if (seenRows.has(rowKey)) {
          return false;
        }

        seenRows.add(rowKey);
        return true;
      });
  }

  getBackgroundColor(value: string): string {
    return setBackgroudTable(value);
  }

  closeDetailPanel(): void {
    this.olMapService.closeDetailPanel();
    this.cdr.detectChanges();
  }

  constructor(
    public olMapService: OLMapService,
    private activatedRoute: ActivatedRoute,
   private zone: NgZone,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(this.activatedRoute.queryParams.subscribe(qp => {
      this.olMapService.mapConfig = qp;
      this.cdr.markForCheck();
    }));
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    this.createMap();
    this.subscriptions.push(this.olMapService.detailChanges$.subscribe(() => this.cdr.markForCheck()));
    if (this.mapElement?.nativeElement && typeof ResizeObserver !== 'undefined') {
      this.mapResizeObserver = new ResizeObserver(() => this.olMapService.map?.updateSize());
      this.mapResizeObserver.observe(this.mapElement.nativeElement);
    }
    this.setOldLayers();
    this.setProj4Defs();
    this.setClickHandler();
  }

  createMap() {
    this.olMapService.createMap();
  }
  setProj4Defs() {
    this.olMapService.setProj4Defs();
  }
  setOldLayers() {
    this.olMapService.layers.forEach((layer) => {
      this.olMapService.map.addLayer(layer.layer);
    });
  }
  private setClickHandler(): void {
    this.clickKey = this.olMapService.map.on('singleclick', (event) => {
      this.zone.run(() => {
        this.olMapService.handleMapClick({
          map: event.map,
          coordinate: event.coordinate,
          originalEvent: event.originalEvent as MouseEvent | undefined,
          source: 'ol',
        });
        this.cdr.detectChanges();
      });
    });

  }
  ngOnDestroy(): void {
    if (this.clickKey) unByKey(this.clickKey);
   this.mapResizeObserver?.disconnect();
    unsubscribe(this.subscriptions);
  }
}
